import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { NikoProjectExtended } from '../../types/project';

gsap.registerPlugin(ScrollTrigger);

interface ProjectChallengesProps {
  project: NikoProjectExtended;
}

export function ProjectChallenges({ project }: ProjectChallengesProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ctx: gsap.Context;

    const timer = setTimeout(() => {
      ctx = gsap.context(() => {
        gsap.fromTo(
          '.challenge-card',
          { y: 60, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 80%',
            },
          }
        );
        ScrollTrigger.refresh();
      }, sectionRef);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, []);

  if (!project.challenges || project.challenges.length === 0) {
    return null;
  }

  return (
    <section ref={sectionRef} className="py-24 px-8 md:px-12 lg:px-24 bg-white/[0.02]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-12">
          Herausforderungen
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          {project.challenges.map((challenge, index) => (
            <div
              key={index}
              className="challenge-card group p-8 border border-white/10 rounded-xl hover:border-emerald-500/30 transition-colors"
            >
              {/* Number */}
              <span className="text-6xl font-light text-emerald-500/20 group-hover:text-emerald-500/40 transition-colors">
                {String(index + 1).padStart(2, '0')}
              </span>

              {/* Title */}
              <h3 className="text-xl text-white font-medium mt-4 mb-4 group-hover:text-emerald-500 transition-colors">
                {challenge.title}
              </h3>

              {/* Description */}
              <p className="text-white/50 leading-relaxed">
                {challenge.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
